import { useLazyQuery } from "@apollo/client";
import { Button, Grid, TextField, Typography, useMediaQuery } from "@material-ui/core";
import boxImage from "assets/images/Box/package.png";
import Dialog from "components/Dialog";
import { DefaultInfo } from "components/NothingRight";
import { StatusHistory } from "components/PaginationList/Styles";
import { Slider } from "components/Slider/Slider";
import Table from "components/Table";
import { ROW_PER_PAGE_OPTIONS } from "config";
import { theme } from "config/theme";
import { statusColorPrinter } from "constants/colors";
import useText from "constants/text";
import { NewJobContext } from "domains/Job/context";
import ExportFormModal from "domains/Task/components/History/ExportFormModal";
import { JobRightSide } from "domains/Task/components/Job/JobRightSide";
import { JobsByClientId } from "domains/Task/graphql/queries";
import { useJobColumns } from "domains/Task/types/Column";
import { Job, JobStatus, JobType } from "generated/graphql";
import { useCurrentClient, useCurrentUser } from "hooks";
import moment from "moment";
import React from "react";
import { useNavigate } from "react-router-dom";
import VectorButton from "ui/Button/vector";
import FilterGroup from "ui/Filter";
import { VerticalAlignTop } from "ui/Icons";
import { historyStyles } from "./styles";
import { FilterStatus } from "./utils/filterByStatus";

export type DateQuery = {
  dateFrom?: string;
  dateTo?: string;
};

const defaultDate: DateQuery = {
  dateFrom: moment().format("YYYY-MM-DD"),
  dateTo: moment().add(1, "days").format("YYYY-MM-DD"),
};
export function HistoryPage() {
  const styles = historyStyles();
  const { text } = useText();
  const { user } = useCurrentUser();
  const { client } = useCurrentClient();
  const [jobDetail, setJobDetail] = React.useState<Job>(null);
  const { setJob, setOrders } = React.useContext(NewJobContext);
  const navigate = useNavigate();
  const [formValues, setFormValues] = React.useState(defaultDate);
  const [dataToSend, setDataToSend] = React.useState<DateQuery>(null);
  const [page, setPage] = React.useState(0);
  const columns = useJobColumns();
  const [allData, setAllData] = React.useState(null);
  const [searchFilter, setSearchFilter] = React.useState(null);
  const [queryJobsByDate, { data }] = useLazyQuery(JobsByClientId);
  const [, setDisplayParcels] = React.useState(false);
  const [showFilter, setShowFilter] = React.useState(false);
  const [pickupChecked, setPickupChecked] = React.useState(false);
  const [dropOffChecked, setDropOffChecked] = React.useState(false);
  const [fromStockChecked, setFromStockChecked] = React.useState(false);
  const [openModal, setOpenModal] = React.useState(false);
  const [typeJob] = React.useState("");
  const [exportFilter, setExportFilter] = React.useState(false);

  const [countTotal, setCountTotal] = React.useState(0);
  const [currentPage, setCurrentPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [, setIsSubmitted] = React.useState(false);
  const [openSlider, setOpenSlider] = React.useState(false);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPickupChecked(event.target.checked);
    setDropOffChecked(false);
    setFromStockChecked(false);
  };

  const handleChangeDrop = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDropOffChecked(event.target.checked);
    setPickupChecked(false);
    setFromStockChecked(false);
  };

  const handleChangeFrom = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFromStockChecked(event.target.checked);
    setPickupChecked(false);
    setDropOffChecked(false);
  };
  const handleConfirm = () => {
    if (pickupChecked === true) {
      queryJobsByDate({
        variables: {
          params: { clientID: client._id },
          queries: { page: page + 1, limit: rowsPerPage, type: JobType.Pickup },
        },
      });
    } else if (dropOffChecked === true) {
      queryJobsByDate({
        variables: {
          params: { clientID: client._id },
          queries: { page: page + 1, limit: rowsPerPage, type: JobType.Dropoff },
        },
      });
    } else if (fromStockChecked === true) {
      queryJobsByDate({
        variables: {
          params: { clientID: client._id },
          queries: { page: page + 1, limit: rowsPerPage, type: JobType.FromStock },
        },
      });
    } else {
      queryJobsByDate({
        variables: { params: { clientID: client._id }, queries: { page, limit: rowsPerPage } },
      });
    }
    setSearchFilter(data);
    setShowFilter(false);
  };

  React.useEffect(() => {
    if (typeJob) {
      queryJobsByDate({
        variables: {
          params: { clientID: client._id },
          queries: {
            page,
            limit: rowsPerPage,
            type: typeJob,
            status: JobStatus.Done,
          },
        },
      });
      setSearchFilter(data);
    } else {
      queryJobsByDate({
        variables: {
          params: { clientID: user?.company_user_clientId },
          queries: {
            dateFrom: dataToSend?.dateFrom,
            dateTo: dataToSend?.dateTo,
            page: page + 1,
            limit: rowsPerPage,
            status: JobStatus.Done,
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, dataToSend, rowsPerPage]);

  React.useEffect(() => {
    if (data) {
      if (data.allJobsByClient?.code === 200) {
        setAllData([...data?.allJobsByClient?.data]);
        setCountTotal(data?.allJobsByClient?.countTotal);
        setCurrentPage(data?.allJobsByClient?.currentPage);
      }
    }
  }, [data]);

  const onRowClick = (job: Job) => {
    setJobDetail(job);
    setDisplayParcels(true);
    setOpenSlider(true);
  };
  const onSetRowPerPage = (props: number) => {
    setRowsPerPage(props);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onReload = (job: Job) => {
    const { job_type, job_address, job_slot_start, job_slot_end, job_time, job_vehicle_type } = job;
    setOrders(job?.job_orders);
    setJob({
      job_type,
      job_address,
      job_date: moment().format("YYYY-MM-DD"),
      job_slot_start,
      job_slot_end,
      job_time,
      job_vehicle_type,
    });
    navigate("/parcels/creations");
  };

  const EventsView = React.useCallback(
    () => <JobRightSide orders={jobDetail?.job_orders} showReloadButton />,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [jobDetail]
  );

  const actionFilter = [
    {
      label: text.all.task_type,
      key: "task_type",
    },
    {
      label: text.all.date,
      key: "date",
    },
  ];

  const handleFilter = (key: string) => {
    switch (key) {
      case "task_type":
        setShowFilter(true);
        break;
      case "date":
        setOpenModal(true);
        break;
      default:
        break;
    }
  };

  const tableAction = React.useMemo(
    () => ({
      title: "",
      field: "",
      render: (item: Job) => (
        <span className={styles.actionStyle}>
          <Button className={styles.reloadButton} onClick={() => onReload(item)}>
            {text.all.reload}
          </Button>
        </span>
      ),
    }),
    []
  );

  const tableStatus = React.useMemo(
    () => ({
      field: "job_status",
      title: `${text.all.status}`,

      render: ({ job_status }: Job) => (
        <StatusHistory color={statusColorPrinter({ status: job_status, type: "bg" })}>{job_status}</StatusHistory>
      ),
      paddingRight: 10,
    }),
    []
  );

  const handleClose = () => setShowFilter(false);
  const handleCloseModal = () => setOpenModal(false);

  const dateFrom = (e: React.FocusEvent<HTMLInputElement>) => {
    setFormValues({
      ...formValues,
      dateFrom: e.target.value,
    });
  };
  const dateTo = (e: React.FocusEvent<HTMLInputElement>) => {
    setFormValues({
      ...formValues,
      dateTo: e.target.value,
    });
  };

  const handleConfirmDate = () => {
    setDataToSend(formValues);
    setOpenModal(false);
    setIsSubmitted(true);
  };

  const handleExport = () => {
    setExportFilter(true);
  };
  const closeExportFilter = () => {
    setExportFilter(false);
  };

  const handleChangePage = (event: React.ChangeEvent<{}>, newpage: number) => {
    setPage(newpage);
  };
  return (
    <>
      <div className={styles.root}>
        <div
          className={styles.left}
          style={{
            width: isSmallScreen ? "100%" : "70%",
          }}
        >
          <Grid container direction="row" className={styles.header}>
            <Grid item xs={7}>
              <Typography className={styles.tag} variant="h3">
                {text.all.choose_the_most}&nbsp;
                <b>{text.all.convenient_delivery}</b>
              </Typography>
            </Grid>

            <Grid item xs={2} className={styles.headerIcon}>
              <img src={boxImage} className={styles.imgHistory} alt="man with laptop" />
            </Grid>
          </Grid>
          <Grid container className={styles.body}>
            <Grid item xs={12} className={styles.headerContainer}>
              <Typography className={styles.bodyTitle}>{text.all.my_history}</Typography>
            </Grid>
            <Grid item xs={12} className={styles.headerAction}>
              <div className={styles.btnExport}>
                <VectorButton icon={<VerticalAlignTop />} handleClick={handleExport} />
              </div>
              <div className={styles.filterButton}>
                <FilterGroup actions={actionFilter} onClick={handleFilter} />
              </div>
            </Grid>
            <Grid className={styles.table}>
              {showFilter && openModal ? (
                <Table
                  rowsIdentifier="_id"
                  tableHeight="50vh"
                  columns={[...columns, tableStatus, tableAction]}
                  data={searchFilter?.allJobsByClient?.data}
                  onRowClick={onRowClick}
                  rowsPerPageOptions={ROW_PER_PAGE_OPTIONS}
                  rowsPerPage={rowsPerPage}
                  currentPage={page}
                  countTotal={countTotal}
                  onChangePage={handleChangePage}
                  setRowsPerPage={onSetRowPerPage}
                />
              ) : (
                <Table
                  rowsIdentifier="_id"
                  columns={[...columns, tableStatus, tableAction]}
                  data={allData}
                  tableHeight="50vh"
                  onRowClick={onRowClick}
                  rowsPerPageOptions={ROW_PER_PAGE_OPTIONS}
                  rowsPerPage={rowsPerPage}
                  currentPage={page}
                  countTotal={countTotal}
                  onChangePage={handleChangePage}
                  setRowsPerPage={onSetRowPerPage}
                />
              )}
              {isSmallScreen && (
                <Slider openDrawer={openSlider} handleClose={() => setOpenSlider(false)}>
                  <JobRightSide orders={jobDetail?.job_orders} showReloadButton />
                </Slider>
              )}
            </Grid>
          </Grid>
        </div>
        <div className={styles.right}>{jobDetail ? <EventsView /> : <DefaultInfo />}</div>
      </div>
      <FilterStatus
        open={showFilter}
        pickupChecked={pickupChecked}
        dropOffChecked={dropOffChecked}
        fromStockChecked={fromStockChecked}
        closed={handleClose}
        confirm={handleConfirm}
        changePick={handleChange}
        changeDrop={handleChangeDrop}
        changeFrom={handleChangeFrom}
      />

      <ExportFormModal open={exportFilter} clientId={client?._id} handleClose={closeExportFilter} />
      <Dialog
        title={text.all.filter_by_date}
        open={openModal}
        maxWidth="xs"
        height="350px"
        handleConfirm={handleConfirmDate}
        handleClose={handleCloseModal}
        confirmButtonTitle={text.all.validate}
        cancelButtonTitle={text.all.cancel}
      >
        <Grid container spacing={1} className={styles.gridStyled}>
          <Grid item xs={12} className={styles.searchDateHistory}>
            <TextField
              type="date"
              variant="outlined"
              label={text.all.from}
              name="dateFrom"
              value={formValues.dateFrom}
              onChange={dateFrom}
              className={styles.textField}
              inputProps={{ max: formValues.dateTo }}
            />
          </Grid>
          <Grid item xs={12} className={styles.searchDateHistory}>
            <TextField
              type="date"
              variant="outlined"
              label={text.all.to}
              name="dateTo"
              value={formValues.dateTo}
              onChange={dateTo}
              className={styles.textField}
              inputProps={{ min: formValues.dateFrom }}
            />
          </Grid>
        </Grid>
      </Dialog>
    </>
  );
}

export default HistoryPage;
